import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { User } from '../models/user';
import { NtripConnection } from '../models/ntrip-connection';
import { environment } from '../../environments/environment';

export type HelpKey = 'userName' | 'uUserName' | 'uPassword' | 'dUserName' | 'dPassword' | 'maxMpNumber' | 'maxClientNumber' | 'active';

export interface HelpDataDetail {
  description: string;
  content: string;
}

export interface RoleItem { id: string; name: string; }

@Injectable({
  providedIn: 'root'
})
export class UserService {

  private getUserUrl = `${environment.apiBaseUrl}/user`;
  private ensureUserUrl = `${environment.apiBaseUrl}/user/ensure`;
  private getUsersUrl = `${environment.apiBaseUrl}/users`;
  private getUsersPagedUrl = `${environment.apiBaseUrl}/users/paged`;
  private getUserByIdBaseUrl = `${environment.apiBaseUrl}/user`;
  private createUserUrl = `${environment.apiBaseUrl}/user`;
  private deleteUserUrl = `${environment.apiBaseUrl}/user`;
  private updateUserUrl = `${environment.apiBaseUrl}/user`;
  private createMountPointUrl = `${environment.apiBaseUrl}/mountpoint`;
  private refreshNtirpConfigUrl = `${environment.apiBaseUrl}/files`;
  private rehashNtirpCasterUrl = `${environment.apiBaseUrl}/ntrip-rehash`;
  private resyncNtirpCasterUrl = `${environment.apiBaseUrl}/ntrip-resync`;
  private getUserPermissionUrl = `${environment.apiBaseUrl}/user-permissions`;
  private getUserRolesUrl = `${environment.apiBaseUrl}/user-roles`;
  private removeUserRoleUrl = `${environment.apiBaseUrl}/user-roles/remove`;
  private getNtripServerUrl = `${environment.apiBaseUrl}/ntrip-server`;
  private ntripApplyUrl = `${environment.apiBaseUrl}/ntrip-apply`;
  private getFilteredUsersUrl = `${environment.apiBaseUrl}/filtered-users`;
  private getUserNtripConnectionsUrl = `${environment.apiBaseUrl}/ntrip-connections/user`;
  private getAllNtripConnectionsUrl = `${environment.apiBaseUrl}/ntrip-connections`;
  private startAnalysisUrl = `${environment.apiBaseUrl}/ntrip-analysis/start`;
  private getAnalysisReportUrl = `${environment.apiBaseUrl}/ntrip-analysis/report`;
  private deleteMyNtripDataUrl = `${environment.apiBaseUrl}/ntrip-analysis/delete-my-data`;
  private publicSiteUrlsUrl = `${environment.apiBaseUrl}/public/site-urls`;

  private adminSyncLastLoginUrl = `${environment.apiBaseUrl}/admin/users/last-login/sync`;
  private adminInactiveListUrl = `${environment.apiBaseUrl}/admin/users/inactive`;
  private adminInactiveCountUrl = `${environment.apiBaseUrl}/admin/users/inactive/count`;
  private adminS3PurgeStartUrl = `${environment.apiBaseUrl}/admin/s3/purge`;
  private adminS3PurgeStatusBaseUrl = `${environment.apiBaseUrl}/admin/s3/purge`;

  // Shared help data
  readonly helpData: Record<HelpKey, HelpDataDetail> = {
    userName: { description: "User's Profile Name", content: "text" },
    uUserName: { description: "UserName for uploading. This is for RTK Base / NTRIP Server connects to this NTRIP Caster Mountpoint. This field is only applicable when using NTRIP 2.0.", content: "text" },
    uPassword: { description: "Password for uploading. This is for RTK Base / NTRIP Server connects to this NTRIP Caster Mountpoint.", content: "text" },
    dUserName: { description: "UserName for downloading. This is for RTK Rovers / NTRIP clients connect to this NTRIP Caster Mountpoint.", content: "text" },
    dPassword: { description: "Password for downloading. This is for RTK Rovers / NTRIP clients connect to this NTRIP Caster Mountpoint.", content: "text" },
    maxMpNumber: { description: "This indicates the maximum number of mountpoints allowed for your account.", content: "number" },
    maxClientNumber: { description: "This indicates the maximum number of NTRIP clients / RTK Rovers allowed concurrently for your account.", content: "number" },
    active: { description: "NTRIP service is exclusively available to users with active accounts.", content: "boolean" }
  };
  
  private lastEnsureCreated = false;

  constructor(private http: HttpClient) { }

  // === Admin inactive users APIs ===
  syncUsersLastLogin(): Observable<any> { return this.http.post(this.adminSyncLastLoginUrl, {}); }
  listInactiveUsers(before: string): Observable<any[]> { return this.http.get<any[]>(this.adminInactiveListUrl, { params: { before } }); }
  countInactiveUsers(before: string): Observable<any> { return this.http.get<any>(this.adminInactiveCountUrl, { params: { before } }); }
  deleteInactiveUsers(before: string): Observable<any> { return this.http.delete<any>(this.adminInactiveListUrl, { params: { before } }); }

  // === Admin S3 purge APIs ===
  startS3DataPurge(date: string): Observable<{ taskId: string }> {
    return this.http.post<{ taskId: string }>(this.adminS3PurgeStartUrl, {}, { params: { date } });
  }

  getS3DataPurgeStatus(taskId: string): Observable<any> {
    const encoded = encodeURIComponent(taskId);
    return this.http.get<any>(`${this.adminS3PurgeStatusBaseUrl}/${encoded}`);
  }

  getUsers(): Observable<any> {
    return this.http.get(this.getUsersUrl);
  }

  getUser(): Observable<User[]> {
    return this.http.get<{ user: User; created: boolean }>(this.ensureUserUrl).pipe(
      map(res => {
        this.lastEnsureCreated = !!res?.created;
        const user = res?.user;
        if (user) {
          return [user];
        }
        return [];
      })
    );
  }
  wasLastEnsureCreated(): boolean {
    return this.lastEnsureCreated;
  }


  getUserById(userId: string): Observable<User> {
    const encodedUserId = encodeURIComponent(userId);
    return this.http.get<User>(`${this.getUserByIdBaseUrl}/${encodedUserId}`);
  }

  getUserPermissions(): Observable<any> {
    return this.http.get(this.getUserPermissionUrl);
  }

  getUserRoles(userId: string): Observable<RoleItem[]> {
    return this.http.get<RoleItem[]>(this.getUserRolesUrl, { params: { userId } });
  }

  removeUserRole(userId: string, roleId: string): Observable<any> {
    return this.http.post(this.removeUserRoleUrl, { userId, roleId });
  }

  addUserRole(userId: string, roleName: string): Observable<any> {
    return this.http.post(`${environment.apiBaseUrl}/user-roles/add`, { userId, roleName });
  }

  createUser(): Observable<any> {
    return this.http.post(this.createUserUrl, {}, {
      observe: 'response'
    });
  } 

  deleteUser(user: User): Observable<any> {
    const encodedUserId = encodeURIComponent(user.userId.toString());
    return this.http.delete(this.deleteUserUrl + '/' + encodedUserId);
  }
  
  /** Admin: update any local user by id. */
  updateUser(user: User): Observable<any> {
    const encodedUserId = encodeURIComponent(user.userId.toString());
    return this.http.put(this.updateUserUrl + '/' + encodedUserId, user, {
      observe: 'response'
    });
  }

  /** Signed-in user updates their own record (credentials, mountpoints, etc.). */
  updateCurrentUser(user: User): Observable<any> {
    return this.http.put(this.updateUserUrl, user, {
      observe: 'response'
    });
  }
  
  generateNewMountPoint(): Observable<any> {    
    return this.http.post(this.createMountPointUrl, {}, {
      observe: 'response'
    });
  }
  
  refreshNtirpConfig(): Observable<any> {
    return this.http.post(this.refreshNtirpConfigUrl, {}, {
      observe: 'response'
    });
  }
  
  rehashNtrip(): Observable<any> {
    return this.http.get(this.rehashNtirpCasterUrl);
  }
  
  resyncNtrip(): Observable<any> {
    return this.http.get(this.resyncNtirpCasterUrl);
  }
  
  getNtripServer(): Observable<any> {
    return this.http.get(this.getNtripServerUrl);
  }
  
  ntripApply(): Observable<any> {
    return this.http.get(this.ntripApplyUrl);
  }

  // Shared method to handle NTRIP apply with logging
  ntripApplyWithLogging(): void {
    this.ntripApply().subscribe({
      next: () => {
        console.info('Applied to NTRIP caster successfully.');
      },
      error: (error) => {
        // In some environments, the rehash endpoint may not be reachable; treat as warning
        console.warn('Apply to NTRIP caster failed (non-critical in local/dev).', error?.message || error);
      }
    });
  }

  getFilteredUsers(keywords: string): Observable<User[]> {    
    return this.http.get<User[]>(this.getFilteredUsersUrl, { params: { keywords } });
  }

  getUsersPaged(page: number, size: number, keywords?: string): Observable<any> {
    const params: any = { page: String(page), size: String(size) };
    if (keywords && keywords.trim()) params.keywords = keywords.trim();
    return this.http.get<any>(this.getUsersPagedUrl, { params });
  }

  getUserNtripConnections(): Observable<NtripConnection[]> {
    return this.http.get<NtripConnection[]>(this.getUserNtripConnectionsUrl);
  }

  getAllNtripConnections(): Observable<NtripConnection[]> {
    return this.http.get<NtripConnection[]>(this.getAllNtripConnectionsUrl);
  }

  getNtripLogLatest(type: 'access' | 'ntripcaster' | 'usage'): Observable<{
    type: string;
    fileName: string;
    fileSizeBytes: number;
    tailLines: number;
    returnedLines: number;
    truncated: boolean;
    content: string;
  }> {
    return this.http.get<any>(`${environment.apiBaseUrl}/admin/ntrip-logs/${type}/latest`);
  }

  downloadNtripLogsZip(type: 'access' | 'ntripcaster' | 'usage'): Observable<Blob> {
    return this.http.get(`${environment.apiBaseUrl}/admin/ntrip-logs/${type}/download-all`, {
      responseType: 'blob'
    });
  }

  startAnalysis(mountpoint: string, durationSec: number, overrides?: { server?: string; username?: string; password?: string; userId?: string }): Observable<any> {
    const body: any = { mountpoint, durationSec };
    if (overrides) {
      if (overrides.server) body.server = overrides.server;
      if (overrides.username) body.username = overrides.username;
      if (overrides.password) body.password = overrides.password;
      if (overrides.userId) body.userId = overrides.userId;
    }
    return this.http.post(this.startAnalysisUrl, body, { observe: 'response' });
  }

  getAnalysisReport(mountpoint: string): Observable<any> {
    return this.http.get(this.getAnalysisReportUrl, { params: { mountpoint } });
  }

  deleteMyNtripData(): Observable<any> {
    return this.http.post(this.deleteMyNtripDataUrl, {});
  }

  // Public (no-auth) site URL config used by landing menus
  getPublicSiteUrls(): Observable<{ mainSiteURL?: string; rtkExpertSiteURL?: string }> {
    return this.http.get<{ mainSiteURL?: string; rtkExpertSiteURL?: string }>(this.publicSiteUrlsUrl);
  }

  getSiteAccessSettings(): Observable<{
    publicAccessEnabled: boolean;
    inviteCode?: string;
    updatedAt?: string;
    updatedBy?: string;
  }> {
    return this.http.get<any>(`${environment.apiBaseUrl}/admin/site-access`);
  }

  updateSiteAccessSettings(body: { publicAccessEnabled: boolean; inviteCode: string }): Observable<{
    publicAccessEnabled: boolean;
    inviteCode?: string;
    updatedAt?: string;
    updatedBy?: string;
  }> {
    return this.http.put<any>(`${environment.apiBaseUrl}/admin/site-access`, body);
  }
}
